import { useQuery } from "@tanstack/react-query";
import ProductShareView, {
    type ShareConfigItem,
} from "@univdiam/ui/customComponents/CatalogProduct/ProductShareView";
import type { ProductImage } from "@univdiam/ui/type";
import { normalizeUrl } from "@univdiam/ui/lib/UrlNormalize";
import { blindMediaUrl } from "@univdiam/ui/lib/blindMedia";
import axios from "axios";
import { useParams } from "react-router-dom";

const resolveShareToken = (token: string) => {
    return axios.get(
        `${import.meta.env.VITE_SERVER_URL}/api/catalog/share/${token}`
    );
};

const ShareProductForCatalog = () => {
    const { token } = useParams();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["product-share-catalog", token],
        queryFn: async () => resolveShareToken(token!),
        enabled: !!token,
        retry: false,
    });

    const shareData = data?.data?.data;

    if (isLoading || isError) {
        return (
            <ProductShareView
                images={[]}
                config={[]}
                isLoading={isLoading}
                isError={isError}
                errorStatus={(error as any)?.response?.status}
            />
        );
    }

    if (!shareData) return null;

    // Parse product images from media_attachment
    const productImages: ProductImage[] = (() => {
        const urls = Array.isArray(shareData.media_attachment)
            ? shareData.media_attachment
            : [];
        return urls.map((url: string, i: number) => ({
            src: blindMediaUrl(normalizeUrl(url)),
            alt: `Product image ${i + 1}`,
            // Small thumbs uploaded by seed script at catalog/thumbs/<filename>
            thumbnail: blindMediaUrl(url.toLowerCase().endsWith(".mp4")
                ? normalizeUrl(url)
                : normalizeUrl(url.replace("/catalog/", "/catalog/thumbs/"))),
        }));
    })();

    const config: ShareConfigItem[] = [
        shareData?.diameter && { label: "Diameter", value: shareData.diameter },
        shareData?.carat_weight && {
            label: "Carat Weight",
            value: shareData.carat_weight,
        },
        shareData?.diamond_quality && {
            label: "Diamond Quality",
            value: shareData.diamond_quality,
        },
        shareData?.metal_type && {
            label: "Metal Type",
            value: shareData.metal_type,
        },
    ].filter(Boolean) as ShareConfigItem[];

    return (
        <ProductShareView
            images={productImages}
            iframeLink={shareData?.iframe_link ?? undefined}
            title={shareData?.title}
            price={shareData?.price ? Number(shareData.price) * Number(shareData?.quantity || 1) : undefined}
            config={config}
            footerRows={
                shareData?.quantity ? (
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                        <label className="text-gray-600 font-medium mb-2 lg:mb-0">
                            Quantity:
                        </label>
                        <span className="font-medium">{shareData.quantity}</span>
                    </div>
                ) : null
            }
        />
    );
};

export default ShareProductForCatalog;
